import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Users2, FolderKanban, Ticket, Newspaper, Mail, TrendingUp, Activity, CheckCircle2, AlertCircle,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const STATS = [
  { key: "leads_new", label: "New leads", icon: Users2, color: "text-primary" },
  { key: "active_projects", label: "Active projects", icon: FolderKanban, color: "text-accent" },
  { key: "tickets_open", label: "Open tickets", icon: Ticket, color: "text-chart-4" },
  { key: "customers", label: "Customers", icon: TrendingUp, color: "text-success" },
  { key: "posts", label: "Published posts", icon: Newspaper, color: "text-primary" },
  { key: "subscribers", label: "Subscribers", icon: Mail, color: "text-accent" },
];

export default function AdminDashboard() {
  const { data } = useQuery({ queryKey: ["admin-dashboard"], queryFn: async () => (await api.get("/admin/dashboard")).data });
  const { data: health } = useQuery({ queryKey: ["admin-health"], queryFn: async () => (await api.get("/admin/health-detail")).data });
  const stats = data?.stats || {};
  const pipeline = Object.entries(data?.pipeline || {}).map(([name, value]) => ({ name, value }));
  const colors = ["#3B82F6", "#06B6D4", "#8B5CF6", "#f59e0b", "#a855f7", "#10B981", "#ef4444", "#64748b"];

  return (
    <div>
      <h1 className="font-display text-2xl font-bold">Dashboard</h1>
      <p className="text-sm text-muted-foreground">Complete operational overview of Buddy Solution.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {STATS.map((s) => (
          <div key={s.key} className="bs-card p-5" data-testid={`admin-stat-${s.key}`}>
            <s.icon className={`h-5 w-5 ${s.color}`} />
            <p className="mt-3 font-display text-3xl font-black">{stats[s.key] ?? 0}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="bs-card p-6">
          <h2 className="font-display text-lg font-bold">Lead pipeline</h2>
          <div className="mt-4 h-64" style={{ minWidth: 280 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={280} minHeight={220}>
              <BarChart data={pipeline}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {pipeline.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bs-card p-6">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Activity className="h-5 w-5 text-primary" /> System health</h2>
            <div className="mt-4 space-y-2.5 text-sm">
              {[["Backend", health?.backend], ["Database", health?.database], ["API", health?.api], ["Email", health?.email]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="flex items-center gap-1.5 font-medium">
                    {v && !String(v).includes("not") && v !== "error" ? <CheckCircle2 className="h-4 w-4 text-success" /> : <AlertCircle className="h-4 w-4 text-chart-4" />}
                    {v || "—"}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Payments & analytics show "requires configuration" until connected.</p>
          </div>
        </div>
      </div>

      <div className="mt-6 bs-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Recent leads</h2>
          <Link to="/admin/leads" className="text-sm font-semibold text-primary">View all</Link>
        </div>
        <div className="mt-4 space-y-2">
          {(data?.recent_leads || []).map((l) => (
            <div key={l.id} className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
              <div><span className="font-semibold">{l.name}</span> <span className="text-muted-foreground">· {l.email}</span></div>
              <div className="flex items-center gap-2"><Badge variant="outline">{l.type}</Badge><Badge variant="secondary">{l.status}</Badge></div>
            </div>
          ))}
          {(data?.recent_leads || []).length === 0 && <p className="text-sm text-muted-foreground">No leads yet.</p>}
        </div>
      </div>
    </div>
  );
}
